import { ApiEndpoints } from "../apiEndpoints";
import { requestApi } from "../apiService";
import {
  BillingInterval,
  CheckoutSessionDetails,
  CreditBalanceResponse,
  Invoice,
  ISubscription,
  SubscriptionPlan,
} from "../types/subscription";

type PaidPlan = Exclude<SubscriptionPlan, "free">;
type PaidInterval = Exclude<BillingInterval, null>;

const STRIPE_ALLOWED_HOSTS = ["checkout.stripe.com", "billing.stripe.com"];

function assertStripeUrl(url: string): void {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    throw new Error("Invalid Stripe redirect URL");
  }
  if (
    parsed.protocol !== "https:" ||
    !STRIPE_ALLOWED_HOSTS.includes(parsed.hostname)
  ) {
    throw new Error(`Untrusted Stripe redirect URL: ${parsed.hostname}`);
  }
}

export const createCheckoutSession = async (
  plan: PaidPlan,
  interval: PaidInterval,
): Promise<string> => {
  const response = await requestApi({
    url: ApiEndpoints.STRIPE_CHECKOUT,
    method: "POST",
    data: { plan, interval },
  });
  const url = response.data.url as string;
  assertStripeUrl(url);
  return url;
};

export type PortalFlow = "subscription_cancel";

export const createPortalSession = async (
  flow?: PortalFlow,
): Promise<string> => {
  const response = await requestApi({
    url: ApiEndpoints.STRIPE_PORTAL,
    method: "POST",
    data: flow ? { flow } : undefined,
  });
  const url = response.data.url as string;
  assertStripeUrl(url);
  return url;
};

export const fetchSubscription = async (): Promise<ISubscription> => {
  const response = await requestApi({
    url: ApiEndpoints.STRIPE_SUBSCRIPTION,
    method: "GET",
  });
  return response.data as ISubscription;
};

export const fetchInvoices = async (): Promise<Invoice[]> => {
  const response = await requestApi({
    url: ApiEndpoints.STRIPE_INVOICES,
    method: "GET",
  });
  return response.data as Invoice[];
};

export const fetchCreditBalance = async (): Promise<CreditBalanceResponse> => {
  const response = await requestApi({
    url: ApiEndpoints.STRIPE_CREDIT_BALANCE,
    method: "GET",
  });
  return response.data as CreditBalanceResponse;
};

export const fetchCheckoutSession = async (
  sessionId: string,
): Promise<CheckoutSessionDetails> => {
  const response = await requestApi({
    url: `${ApiEndpoints.STRIPE_SESSION}/${sessionId}`,
    method: "GET",
  });
  return response.data as CheckoutSessionDetails;
};
